import { useState } from "react";
import Animation from "../Animation";
import { useAnimationWorkflow } from "@/lib/hooks/useAnimationWorkflow";
import { CardName, getRandomTarotCard } from "@/lib/constants/tarot";
import {
  bounceWorkflow,
  fadeInUpWorkflow,
  scaleInWorkflow,
  createSlideWorkflow,
  complexCardRevealWorkflow,
} from "./examples/animationExamples";
import {
  fadeInWorkflow,
  verticalFlipWorkflow,
  shrinkWorkflow,
  shrinkBounceWorkflow,
  cardMoveWorkflow,
} from "@/lib/utils/animation/animationSteps";
import {
  shrinkFlipWorkflow,
  flipAtCenterWorkflow,
  cardRevealWorkflow,
  cardFlipWorkflow,
  cardDrawWorkflow,
} from "@/lib/utils/animation/animationWorkflows";
import CardImage from "../CardImage";
import { Button } from "../ui/button";

function AnimationWorkflowDemoPage() {
  const [currentCard, setCurrentCard] = useState<CardName | null>(null);
  const [selectedExample, setSelectedExample] = useState<string>("");
  const [preserveAnimationState, setPreserveAnimationState] = useState(true);
  const [showComponentAPI, setShowComponentAPI] = useState(false);

  // Animation workflow hook
  const {
    startWorkflow,
    animatedStyle,
    isRunning,
    currentStep,
    resetWorkflow,
  } = useAnimationWorkflow();

  const examples = [
    {
      id: "simple-fade",
      name: "Simple Fade In",
      description: "Basic fade in animation (500ms)",
      complexity: "Simple",
      workflow: fadeInWorkflow(500),
    },
    {
      id: "simple-bounce",
      name: "Bounce Effect",
      description: "Two-step bounce animation with scale",
      complexity: "Simple",
      workflow: bounceWorkflow,
    },
    {
      id: "fade-in-up",
      name: "Fade In Up",
      description: "Fade in with upward slide motion",
      complexity: "Simple",
      workflow: fadeInUpWorkflow(600),
    },
    {
      id: "scale-in",
      name: "Scale In",
      description: "Scale animation with bounce easing",
      complexity: "Simple",
      workflow: scaleInWorkflow(1, 400),
    },
    {
      id: "vertical-flip",
      name: "Vertical Flip",
      description: "Card flips around vertical center like turning a page",
      complexity: "Simple",
      workflow: verticalFlipWorkflow(2000),
    },
    {
      id: "shrink",
      name: "Shrink",
      description: "Scale down and stay shrunken",
      complexity: "Simple",
      workflow: shrinkWorkflow(300),
    },
    {
      id: "shrink-bounce",
      name: "Shrink Bounce",
      description: "Quick press-down effect like a button touch",
      complexity: "Simple",
      workflow: shrinkBounceWorkflow(200),
    },
    {
      id: "shrink-flip",
      name: "Shrink + Flip",
      description: "Shrink bounce followed by vertical flip",
      complexity: "Medium",
      workflow: shrinkFlipWorkflow(200, 2000),
    },
    {
      id: "flip-at-center",
      name: "180° Flip at Vertical Center",
      description: "2-step flip: pause at 90°, then complete to 180°",
      complexity: "Medium",
      workflow: flipAtCenterWorkflow(2000),
    },
    {
      id: "card-reveal",
      name: "Card Reveal",
      description:
        "Turn 90° then reveal front like turning a card from back to front",
      complexity: "Medium",
      workflow: cardRevealWorkflow(() => {
        const newCard = getRandomTarotCard();
        setCurrentCard(newCard);
        console.log("Card revealed:", newCard);
      }, 2000),
    },
    {
      id: "card-flip",
      name: "Card Flip",
      description: "Two-step card flip with opacity changes",
      complexity: "Medium",
      workflow: cardFlipWorkflow(() => {
        const newCard = getRandomTarotCard();
        setCurrentCard(newCard);
        console.log("Card revealed:", newCard);
      }),
    },
    {
      id: "card-move",
      name: "Card Movement",
      description: "Move card to specific position with scaling",
      complexity: "Medium",
      workflow: cardMoveWorkflow("-30vw", "-15vh", 0.6, () => {
        console.log("Card movement complete");
      }),
    },
    {
      id: "custom-slide",
      name: "Custom Slide",
      description: "Configurable slide animation with custom coordinates",
      complexity: "Medium",
      workflow: createSlideWorkflow({
        fromX: 0,
        fromY: 50,
        toX: 100,
        toY: -20,
        duration: 800,
        easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        onComplete: () => console.log("Custom slide complete!"),
      }),
    },
    {
      id: "full-card-draw",
      name: "Complete Card Draw",
      description: "Full card drawing sequence with all effects",
      complexity: "Complex",
      workflow: cardDrawWorkflow(
        () => {
          const newCard = getRandomTarotCard();
          setCurrentCard(newCard);
          console.log("Card revealed in full sequence:", newCard);
        },
        () => {
          console.log("Full card draw sequence complete");
        },
      ),
    },
    {
      id: "complex-reveal",
      name: "Complex Card Reveal",
      description: "6-step animation with glow, flip, move, and fade effects",
      complexity: "Complex",
      workflow: complexCardRevealWorkflow(
        () => console.log("Complex animation started"),
        () => {
          const newCard = getRandomTarotCard();
          setCurrentCard(newCard);
          console.log("Card revealed in complex sequence:", newCard);
        },
        () => console.log("Card positioned"),
        () => console.log("Complex animation complete"),
      ),
    },
  ];

  const runExample = (example: (typeof examples)[0]) => {
    if (isRunning) {
      console.log("Animation already running, ignoring click");
      return;
    }

    console.log("Starting example:", example.id);

    // Force reset first
    resetWorkflow();

    setSelectedExample(example.id);

    // Reset card if needed
    if (example.id.includes("card") || example.complexity === "Complex") {
      setCurrentCard(null);
    }

    // Create workflow with completion callback
    const workflowWithCallback = {
      ...example.workflow,
      onWorkflowComplete: () => {
        // Call original completion callback if it exists
        if (example.workflow.onWorkflowComplete) {
          example.workflow.onWorkflowComplete();
        }

        // Auto-reset if preserve state is disabled
        if (!preserveAnimationState) {
          setTimeout(() => {
            resetWorkflow();
            setCurrentCard(null);
            setSelectedExample("");
          }, 500); // Small delay to show completed state
        }
      },
    };

    // Small delay to ensure reset is processed
    setTimeout(() => {
      console.log("Starting workflow after reset delay");
      startWorkflow(workflowWithCallback);
    }, 50);
  };

  const handleReset = () => {
    resetWorkflow();
    setCurrentCard(null);
    setSelectedExample("");
  };

  return (
    <div className="animation-demo-container h-screen flex flex-col p-4">
      <div className="animation-demo-content w-full max-w-7xl mx-auto flex flex-col h-full">
        {/* Header */}
        <div className="demo-header mb-4 text-center flex-shrink-0">
          <h2 className="demo-page-title text-xl sm:text-2xl font-light mb-2">
            Animation Workflow Demo
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            Interactive examples of the animation workflow system
          </p>

          {/* API Toggle */}
          <div className="mt-3">
            <Button
              onClick={() => setShowComponentAPI(!showComponentAPI)}
              variant="outline"
              size="sm"
              className={`${
                showComponentAPI
                  ? "border-blue-400 hover:bg-blue-400"
                  : "border-orange-500 hover:bg-orange-500 ring-2 ring-orange-200"
              } hover:text-white transition-all`}
            >
              {showComponentAPI ? "← Show Hook API" : "Show Component API →"}
            </Button>
          </div>
        </div>

        {/* Main Layout: Left List + Right Display */}
        {!showComponentAPI ? (
          <div className="demo-main-layout flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
            {/* Left Side: Animation Examples List */}
            <div className="examples-sidebar flex-shrink-0 lg:w-80 xl:w-96">
              <div className="examples-container bg-white border border-gray-300 rounded-lg p-4 h-full flex flex-col">
                <h3 className="text-lg font-medium mb-4 flex-shrink-0">
                  Animation Examples
                </h3>
                <div
                  className="examples-scroll flex-1 overflow-y-auto pr-2"
                  style={{ maxHeight: "calc(100vh - 200px)" }}
                >
                  {/* Simple Examples */}
                  <div className="complexity-section mb-6">
                    <h4 className="text-md font-medium text-green-600 mb-3 sticky top-0 bg-white py-2 -mx-2 px-2 z-10">
                      Simple Animations
                    </h4>
                    <div className="examples-list space-y-3">
                      {examples
                        .filter((ex) => ex.complexity === "Simple")
                        .map((example) => (
                          <div
                            key={example.id}
                            className="example-card bg-gray-50 border border-gray-300 p-2 rounded-lg"
                          >
                            <div className="example-header mb-2">
                              <div className="flex items-start justify-between mb-1">
                                <h5 className="font-medium text-xs flex-1 mr-2">
                                  {example.name}
                                </h5>
                                <Button
                                  onClick={() => runExample(example)}
                                  disabled={isRunning}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-auto leading-none flex-shrink-0"
                                >
                                  {isRunning && selectedExample === example.id
                                    ? "..."
                                    : "Run"}
                                </Button>
                              </div>
                              <p className="text-xs text-gray-600">
                                {example.description}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Medium Examples */}
                  <div className="complexity-section mb-6">
                    <h4 className="text-md font-medium text-blue-600 mb-3 sticky top-0 bg-white py-2 -mx-2 px-2 z-10">
                      Medium Complexity
                    </h4>
                    <div className="examples-list space-y-3">
                      {examples
                        .filter((ex) => ex.complexity === "Medium")
                        .map((example) => (
                          <div
                            key={example.id}
                            className="example-card bg-gray-50 border border-gray-300 p-2 rounded-lg"
                          >
                            <div className="example-header mb-2">
                              <div className="flex items-start justify-between mb-1">
                                <h5 className="font-medium text-xs flex-1 mr-2">
                                  {example.name}
                                </h5>
                                <Button
                                  onClick={() => runExample(example)}
                                  disabled={isRunning}
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 h-auto leading-none flex-shrink-0"
                                >
                                  {isRunning && selectedExample === example.id
                                    ? "..."
                                    : "Run"}
                                </Button>
                              </div>
                              <p className="text-xs text-gray-600">
                                {example.description}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Complex Examples */}
                  <div className="complexity-section mb-4">
                    <h4 className="text-md font-medium text-purple-600 mb-3 sticky top-0 bg-white py-2 -mx-2 px-2 z-10">
                      Complex Animations
                    </h4>
                    <div className="examples-list space-y-3">
                      {examples
                        .filter((ex) => ex.complexity === "Complex")
                        .map((example) => (
                          <div
                            key={example.id}
                            className="example-card bg-gray-50 border border-gray-300 p-2 rounded-lg"
                          >
                            <div className="example-header mb-2">
                              <div className="flex items-start justify-between mb-1">
                                <h5 className="font-medium text-xs flex-1 mr-2">
                                  {example.name}
                                </h5>
                                <Button
                                  onClick={() => runExample(example)}
                                  disabled={isRunning}
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 py-1 h-auto leading-none flex-shrink-0"
                                >
                                  {isRunning && selectedExample === example.id
                                    ? "..."
                                    : "Run"}
                                </Button>
                              </div>
                              <p className="text-xs text-gray-600">
                                {example.description}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Animation Display */}
            <div className="animation-display-side flex-1 lg:flex-2">
              <div className="animation-display-container bg-gray-50 border-2 border-black rounded-lg p-8 h-full flex flex-col">
                {/* Status Info - Fixed Height */}
                <div className="animation-status mb-4 text-xs text-gray-600 bg-white p-3 rounded border h-20 flex flex-col justify-center">
                  <div className="status-line">
                    <strong>Status:</strong>{" "}
                    {isRunning
                      ? "Running"
                      : selectedExample
                      ? "Completed"
                      : "Ready"}
                  </div>
                  <div className="step-line">
                    <strong>Step:</strong>{" "}
                    {isRunning
                      ? `${currentStep + 1}`
                      : selectedExample
                      ? "Complete"
                      : "-"}
                  </div>
                  <div className="example-line">
                    <strong>Example:</strong>{" "}
                    {selectedExample || "Select from left"}
                  </div>
                </div>

                {/* Animation Display Area */}
                <div className="animation-display-area flex-1 flex items-center justify-center relative min-h-[400px]">
                  {/* Animated Element */}
                  <div
                    className="animation-target"
                    style={{
                      ...animatedStyle,
                      transformOrigin: "center center",
                    }}
                  >
                    <CardImage
                      cardName={currentCard}
                      className="w-32 h-52 sm:w-36 sm:h-60 lg:w-40 lg:h-64 rounded-lg shadow-lg"
                      alt={currentCard || "Demo card"}
                    />
                  </div>
                </div>

                {/* Controls */}
                <div className="animation-controls mt-4 space-y-3">
                  <div className="control-buttons flex gap-2 justify-center">
                    <Button
                      onClick={handleReset}
                      variant="outline"
                      size="sm"
                      className="border-gray-400 hover:bg-gray-400 hover:text-white"
                    >
                      Reset
                    </Button>
                    <Button
                      onClick={() =>
                        console.log("Current animated style:", animatedStyle)
                      }
                      variant="outline"
                      size="sm"
                      className="border-blue-400 hover:bg-blue-400 hover:text-white"
                    >
                      Log State
                    </Button>
                  </div>

                  <div className="control-options flex items-center justify-center">
                    <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preserveAnimationState}
                        onChange={(e) =>
                          setPreserveAnimationState(e.target.checked)
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>Preserve animation state</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Component API Examples */
          <div className="component-api-demo flex-1">
            <div className="bg-white border border-gray-300 rounded-lg p-6 h-full overflow-y-auto">
              <h3 className="text-xl font-medium mb-6">
                Animation Component API Examples
              </h3>

              {/* Simple API Examples */}
              <div className="api-section mb-8">
                <h4 className="text-lg font-medium text-green-600 mb-4">
                  Simple API
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Fade In */}
                  <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-medium mb-2">Fade In</h5>
                    <code className="text-xs bg-gray-200 p-2 rounded mb-3 block">
                      {'<Animation simple="fade-in">'}
                    </code>
                    <Animation simple="fade-in" key="fade-demo">
                      <div className="w-16 h-24 bg-blue-500 rounded-lg"></div>
                    </Animation>
                  </div>

                  {/* Shrink Bounce */}
                  <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-medium mb-2">Shrink Bounce</h5>
                    <code className="text-xs bg-gray-200 p-2 rounded mb-3 block">
                      {'<Animation simple="shrink-bounce">'}
                    </code>
                    <Animation simple="shrink-bounce" key="shrink-demo">
                      <div className="w-16 h-24 bg-green-500 rounded-lg"></div>
                    </Animation>
                  </div>

                  {/* Vertical Flip */}
                  <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-medium mb-2">Vertical Flip</h5>
                    <code className="text-xs bg-gray-200 p-2 rounded mb-3 block">
                      {'<Animation simple="vertical-flip">'}
                    </code>
                    <Animation simple="vertical-flip" key="flip-demo">
                      <div className="w-16 h-24 bg-purple-500 rounded-lg"></div>
                    </Animation>
                  </div>
                </div>
              </div>

              {/* Steps API Examples */}
              <div className="api-section mb-8">
                <h4 className="text-lg font-medium text-blue-600 mb-4">
                  Steps API
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Simple Steps */}
                  <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-medium mb-2">Multiple Steps</h5>
                    <code className="text-xs bg-gray-200 p-2 rounded mb-3 block">
                      {'<Animation steps={["shrink-bounce", "vertical-flip"]}>'}
                    </code>
                    <Animation
                      steps={["shrink-bounce", "vertical-flip"]}
                      key="steps-demo"
                    >
                      <div className="w-16 h-24 bg-orange-500 rounded-lg"></div>
                    </Animation>
                  </div>

                  {/* Configured Steps */}
                  <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                    <h5 className="font-medium mb-2">Configured Steps</h5>
                    <code className="text-xs bg-gray-200 p-2 rounded mb-3 block">
                      {
                        '<Animation steps={[{type: "fade-in", duration: 1000}]}>'
                      }
                    </code>
                    <Animation
                      steps={[{ type: "fade-in", duration: 1000 }]}
                      key="config-demo"
                    >
                      <div className="w-16 h-24 bg-red-500 rounded-lg"></div>
                    </Animation>
                  </div>
                </div>
              </div>

              {/* Connected Animations */}
              <div className="api-section mb-8">
                <h4 className="text-lg font-medium text-orange-600 mb-4">
                  Connected Animations
                </h4>

                {/* Example 1: Card triggers text fade in */}
                <div className="demo-card bg-gray-50 p-4 rounded-lg border mb-6">
                  <h5 className="font-medium mb-2">Card → Text Fade In</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Click the purple card to flip it and fade in the text
                    message
                  </p>

                  <div className="flex flex-col items-center gap-4">
                    {/* Primary card that triggers text */}
                    <Animation
                      animationId="card-trigger"
                      trigger="click"
                      steps={["vertical-flip", { trigger: "text-fade" }]}
                      className="cursor-pointer"
                      onStart={() =>
                        console.log("Primary card animation started")
                      }
                      onComplete={() =>
                        console.log("Primary card animation completed")
                      }
                    >
                      <div className="w-20 h-32 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg border-2 border-purple-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow">
                        <span className="text-white text-xs font-medium">
                          Click Me
                        </span>
                      </div>
                    </Animation>

                    <span className="text-gray-400">↓</span>

                    {/* Text that should fade in */}
                    <Animation
                      animationId="text-fade"
                      trigger="manual"
                      simple="fade-in"
                      style={{ opacity: 0 }}
                      onStart={() => console.log("Text fade animation started")}
                      onComplete={() =>
                        console.log("Text fade animation completed")
                      }
                    >
                      <div className="px-4 py-2 bg-gradient-to-r from-green-400 to-green-600 rounded-lg text-white font-medium text-sm">
                        ✨ Card was flipped!
                      </div>
                    </Animation>
                  </div>
                </div>

                {/* Example 2: Multi-step chain reaction */}
                <div className="demo-card bg-gray-50 p-4 rounded-lg border">
                  <h5 className="font-medium mb-2">Chain Reaction</h5>
                  <p className="text-sm text-gray-600 mb-3">
                    Click the first card to start a 3-step animation chain
                  </p>

                  <div className="flex gap-4 items-center justify-center flex-wrap">
                    {/* Step 1: Initial trigger */}
                    <Animation
                      animationId="chain-1"
                      trigger="click"
                      steps={["shrink-bounce", { trigger: "chain-2" }]}
                      className="cursor-pointer"
                    >
                      <div className="w-16 h-20 bg-red-500 rounded-lg border-2 border-red-600 flex items-center justify-center">
                        <span className="text-white text-xs">1</span>
                      </div>
                    </Animation>

                    <span className="text-gray-400">→</span>

                    {/* Step 2: Triggered by step 1 */}
                    <Animation
                      animationId="chain-2"
                      trigger="manual"
                      steps={["vertical-flip", { trigger: "chain-3" }]}
                    >
                      <div className="w-16 h-20 bg-blue-500 rounded-lg border-2 border-blue-600 flex items-center justify-center">
                        <span className="text-white text-xs">2</span>
                      </div>
                    </Animation>

                    <span className="text-gray-400">→</span>

                    {/* Step 3: Final in chain */}
                    <Animation
                      animationId="chain-3"
                      trigger="manual"
                      simple="fade-in"
                      style={{ opacity: 0 }}
                      onStart={() =>
                        console.log("Chain step 3 fade animation started")
                      }
                      onComplete={() =>
                        console.log("Chain step 3 fade animation completed")
                      }
                    >
                      <div className="w-16 h-20 bg-green-500 rounded-lg border-2 border-green-600 flex items-center justify-center">
                        <span className="text-white text-xs">3</span>
                      </div>
                    </Animation>
                  </div>

                  <code className="text-xs bg-gray-200 p-2 rounded mt-3 block">
                    {`// Chain: Click 1 → Shrink → Trigger 2 → Flip → Trigger 3 → Fade in
<Animation animationId="chain-1" trigger="click" steps={["shrink-bounce", {trigger: "chain-2"}]}>
  <div>1</div>
</Animation>
<Animation animationId="chain-2" trigger="manual" steps={["vertical-flip", {trigger: "chain-3"}]}>
  <div>2</div>
</Animation>
<Animation animationId="chain-3" trigger="manual" simple="fade-in" style={{ opacity: 0 }}>
  <div>3</div>
</Animation>`}
                  </code>
                </div>
              </div>

              {/* Code Examples */}
              <div className="api-section">
                <h4 className="text-lg font-medium text-purple-600 mb-4">
                  Code Examples
                </h4>
                <div className="space-y-4">
                  <div className="code-example bg-gray-100 p-4 rounded">
                    <h5 className="font-medium mb-2">Simple Animation</h5>
                    <pre className="text-sm overflow-x-auto">
                      {`<Animation simple="shrink-bounce">
  <CardImage cardName="the-fool" />
</Animation>`}
                    </pre>
                  </div>

                  <div className="code-example bg-gray-100 p-4 rounded">
                    <h5 className="font-medium mb-2">Step-based Animation</h5>
                    <pre className="text-sm overflow-x-auto">
                      {`<Animation steps={["shrink-bounce", "vertical-flip"]}>
  <CardImage cardName="the-magician" />
</Animation>`}
                    </pre>
                  </div>

                  <div className="code-example bg-gray-100 p-4 rounded">
                    <h5 className="font-medium mb-2">Configured Steps</h5>
                    <pre className="text-sm overflow-x-auto">
                      {`<Animation
  steps={[
    { type: "fade-in", duration: 800 },
    { type: "card-move", targetX: "-30vw", targetScale: 0.5 }
  ]}
  onComplete={() => console.log('Animation complete!')}
>
  <CardImage cardName="the-empress" />
</Animation>`}
                    </pre>
                  </div>

                  <div className="code-example bg-gray-100 p-4 rounded">
                    <h5 className="font-medium mb-2">
                      Connected Animations with Fade
                    </h5>
                    <pre className="text-sm overflow-x-auto">
                      {`// Primary animation that triggers secondary
<Animation
  animationId="card1"
  trigger="click"
  steps={[
    "shrink-bounce",
    { trigger: "label1" },
    "vertical-flip"
  ]}
>
  <CardImage cardName="the-fool" />
</Animation>

// Secondary fade-in animation triggered by primary
<Animation
  animationId="label1"
  trigger="manual"
  simple="fade-in"
  style={{ opacity: 0 }}
>
  <div className="card-label">The Fool - New Beginnings</div>
</Animation>`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnimationWorkflowDemoPage;
